/**
 * The qualifying questions, per brand.
 *
 * Copy is taken verbatim from the Figma, including its own wording quirks, so
 * that the build and the prototype can be diffed without a false positive on
 * every heading. Anything that should read differently is a copy decision, not
 * a code one — change it here and nowhere else.
 *
 * Each answer is stored under the step key and submitted in the lead's
 * `answers` object, which the API already accepts as free-form JSON.
 */
import {
  Camera,
  Gauge,
  HomeIcon,
  Laptop,
  PlayCircle,
  ShieldCheck,
  Tag,
  Tv,
  User,
  Users,
  UsersThree,
  Wifi,
  WifiOff,
  XCircle,
} from '../components/icons.jsx';

/** Providers are shared by both journeys and rendered as artwork, not icons. */
const PROVIDER_OPTIONS = [
  { value: 'sky', label: 'Sky', logo: '/brand/providers/sky.png' },
  { value: 'ee', label: 'EE', logo: '/brand/providers/ee.png' },
  { value: 'virgin-media', label: 'Virgin Media', logo: '/brand/providers/virgin-media.png' },
  { value: 'talktalk', label: 'TalkTalk', logo: '/brand/providers/talktalk.png' },
  { value: 'plusnet', label: 'Plusnet', logo: '/brand/providers/plusnet.png' },
  { value: 'other', label: 'Other' },
  { value: 'not-sure', label: 'I’m not sure' },
];

const PROVIDER_QUESTION = {
  key: 'provider',
  title: 'Who is your current provider?',
  subtitle: 'Tell us more about your current broadband situation',
  layout: 'logos',
  options: PROVIDER_OPTIONS,
};

const HOME_QUESTIONS = [
  {
    key: 'broadband',
    title: 'Do you currently have broadband?',
    subtitle: 'Tell us more about your current broadband situation',
    layout: 'pair',
    options: [
      { value: 'yes', label: 'Yes, I have broadband.', icon: Wifi, tone: 'yes' },
      { value: 'no', label: 'No, I don’t have broadband.', icon: WifiOff, tone: 'no' },
    ],
  },
  PROVIDER_QUESTION,
  {
    key: 'usage',
    title: 'What do you most use your broadband for?',
    subtitle: 'Tell us about how you use your home broadband.',
    layout: 'grid',
    options: [
      { value: '4k-streaming', label: '4K Video Streaming', icon: Tv },
      { value: 'working-from-home', label: 'Working From Home', icon: Laptop },
      { value: 'social-media', label: 'Browsing Social Media', icon: Camera },
      { value: 'video-gaming', label: 'Video Gaming', icon: PlayCircle },
      { value: 'all', label: 'All of the above' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    key: 'homeowner',
    title: 'Are you the homeowner?',
    subtitle: 'Tell us more about your current household situation',
    layout: 'pair',
    options: [
      { value: 'yes', label: 'Yes, I Am.', icon: HomeIcon, tone: 'yes' },
      { value: 'no', label: 'No, I’m not.', icon: XCircle, tone: 'no' },
    ],
  },
];

const BUSINESS_QUESTIONS = [
  {
    key: 'broadband',
    title: 'Does your business currently have broadband?',
    subtitle: 'Tell us more about your current business broadband situation',
    layout: 'pair',
    options: [
      { value: 'yes', label: 'Yes, we have broadband.', icon: Wifi, tone: 'yes' },
      { value: 'no', label: 'No, we don’t have broadband.', icon: WifiOff, tone: 'no' },
    ],
  },
  PROVIDER_QUESTION,
  {
    key: 'employees',
    title: 'How many employees in your business?',
    subtitle: 'Tell us more about your business broadband needs',
    layout: 'trio',
    options: [
      { value: '1-20', label: '1-20', icon: User },
      { value: '21-100', label: '21-100', icon: Users },
      { value: '100+', label: '100+', icon: UsersThree },
    ],
  },
  {
    key: 'priorities',
    title: 'What features are most important for your business?',
    subtitle: 'Tell us more about your business broadband needs.',
    layout: 'grid',
    options: [
      { value: 'speed', label: 'Super-fast speeds', icon: Gauge },
      { value: 'stability', label: 'Connection Stability', icon: ShieldCheck },
      { value: 'value', label: 'Value For Money', icon: Tag },
      { value: 'all', label: 'All of the above' },
      { value: 'other', label: 'Other' },
    ],
  },
];

export function questionsForBrand(brand) {
  return brand === 'business' ? BUSINESS_QUESTIONS : HOME_QUESTIONS;
}

/**
 * Asking "who is your current provider?" straight after someone has said they
 * have no broadband is a dead end for them and a junk answer for us, so that
 * question is skipped on the "no" path. The prototype draws the flow as a
 * straight line and does not cover the branch.
 */
export function isQuestionSkipped(question, answers) {
  return question.key === 'provider' && answers.broadband === 'no';
}

/** Header tagline and eyebrow, per brand. */
export const BRAND_COPY = {
  home: {
    tagline: 'Home broadband for every need.',
    eyebrow: 'Home Broadband Switching',
    postcodeTitle: 'Tell us your postcode',
    addressTitle: 'Tell us your address',
    callback: 'Want to talk to someone about your switching options?',
    offersFormTitle: 'Enter your details',
  },
  business: {
    tagline: 'Business broadband for every need.',
    eyebrow: 'Business Broadband Switching',
    postcodeTitle: 'Tell us your business postcode',
    addressTitle: 'Tell us your business address',
    callback: 'Want To Talk To Someone About Your Business Options?',
    offersFormTitle: 'Enter your details to unlock exclusive offers!',
  },
};

/**
 * Progress-bar fill per step, read off the Figma frames rather than computed —
 * the designed curve is deliberately uneven (it barely moves between postcode
 * and address, then jumps) and an even n/6 split visibly contradicts it.
 */
export const PROGRESS = {
  postcode: 0.174,
  address: 0.174,
  broadband: 0.334,
  provider: 0.557,
  usage: 0.761,
  employees: 0.761,
  homeowner: 0.889,
  priorities: 0.889,
  offers: 1,
};
